import React, { useMemo, useState } from "react";
import { PassMapSide, PassMapStatus, getPassMapTeamColor } from "./helper";
import { PassMapNode, passMapMocks, PassMapMock } from "./mockPassMapData";
import { CategoryTabs } from "../components/CategoryTabs";
import { PitchLayout } from "../components/PitchLayout";

const PASS_MAP_SIDE_TABS: { id: PassMapSide; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "away", label: "Away" },
];

const toPercentage = (value: number) => `${(value * 100).toFixed(2)}%`;

export type PassMapProps = {
  status?: PassMapStatus;
};

export const PassMap: React.FC<PassMapProps> = ({ status }) => {
  const [activeSide, setActiveSide] = useState<PassMapSide>("home");

  const passMapData: PassMapMock = passMapMocks[activeSide];

  const nodeLookup = useMemo(() => {
    const map = new Map<string, PassMapNode>();
    passMapData.nodes.forEach((node) => map.set(node.id, node));
    return map;
  }, [passMapData]);

  const teamColor = useMemo(
    () => getPassMapTeamColor(status, activeSide),
    [activeSide, status]
  );

  return (
    <div className="flex flex-col w-full">
      <CategoryTabs
        tabs={PASS_MAP_SIDE_TABS}
        selectedCategory={activeSide}
        onSelectCategory={setActiveSide}
      />
      <div className="relative h-full w-full">
        <PitchLayout>
          <div className="relative inset-0 h-full w-full">
            {/* Pass connections */}
            <svg
              className="absolute inset-0 h-full w-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              {(passMapData.connections ?? []).map((connection, index) => {
                const fromNode = nodeLookup.get(connection.source);
                const toNode = nodeLookup.get(connection.target);

                if (!fromNode || !toNode) return null;

                const strokeWidth = (connection.weight ?? 0) * 0.25;

                return (
                  <line
                    key={index}
                    x1={fromNode.x * 100}
                    y1={fromNode.y * 100}
                    x2={toNode.x * 100}
                    y2={toNode.y * 100}
                    stroke="#ffffff"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    opacity={0.96}
                  />
                );
              })}
            </svg>

            {/* Player nodes */}
            <div className="absolute inset-0">
              {passMapData.nodes.map((node) => (
                <div
                  key={node.id}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{
                    left: toPercentage(node.x),
                    top: toPercentage(node.y),
                  }}
                >
                  <div
                    className="flex items-center justify-center rounded-full border-2 border-white text-white font-semibold tracking-wide text-sm shadow-[0_12px_22px_rgba(0,0,0,0.55)] lg:w-11 lg:h-11 w-8 h-8"
                    style={{
                      backgroundColor: teamColor,
                      textShadow:
                        "0 0 2px #000, 0 1px 3px #000, 0 2px 6px #000",
                      WebkitTextStroke: "2px #000",
                      paintOrder: "stroke fill",
                    }}
                  >
                    {node.number}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </PitchLayout>
      </div>

      <div className="pb-[2vw] lg:px-0 flex flex-row w-full items-center justify-start gap-2 lg:gap-4 overflow-x-scroll no-scrollbar">
        {passMapData.playerOrder.map((playerId) => {
          const node = nodeLookup.get(playerId);
          if (!node) return null;
          return (
            <div
              key={playerId}
              className="flex min-w-8 min-h-8 lg:min-h-11 lg:min-w-11 max-w-8 max-h-8 lg:max-h-11 lg:max-w-11 items-center justify-center rounded-full border-2 border-white text-sm font-semibold text-white shadow-[0_10px_18px_rgba(0,0,0,0.35)]"
              style={{
                backgroundColor: teamColor,
                textShadow: "0 0 2px #000, 0 1px 3px #000, 0 2px 6px #000",
                WebkitTextStroke: "2px #000",
                paintOrder: "stroke fill",
              }}
            >
              {node.number}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PassMap;
